import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { reportAuthError } from '@/lib/authRecovery';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

// Errores de token que indican que la sesión quedó inválida (normalmente por
// desfase de hora del dispositivo: "JWT issued at future").
const AUTH_TOKEN_ERROR = /(issued at future|jwt)/i;

// Interceptor global de fetch para el cliente de Supabase. Cuando una petición
// falla con 401/403 por un token inválido, intenta renovar la sesión una vez y
// reintenta la petición original con el token nuevo. Así un desfase de hora no
// rompe la pantalla: se corrige solo.
type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

function withBearer(init: FetchInit, token: string): FetchInit {
  const headers = new Headers(init?.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

async function authAwareFetch(input: FetchInput, init?: FetchInit): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status !== 401 && response.status !== 403) {
    return response;
  }

  const body = await response
    .clone()
    .text()
    .catch(() => '');

  if (!AUTH_TOKEN_ERROR.test(body)) {
    return response;
  }

  const recovered = await reportAuthError();
  if (!recovered || !supabase) {
    return response;
  }

  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return response;
    // Un 401/403 por token inválido no fue procesado por el servidor, así que
    // reintentar es seguro incluso para peticiones de escritura.
    return await fetch(input, withBearer(init, token));
  } catch {
    return response;
  }
}

export const supabase: SupabaseClient | null = isSupabaseConfigured
  ? createClient(supabaseUrl as string, supabaseAnonKey as string, {
      global: {
        fetch: authAwareFetch,
      },
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        flowType: 'pkce',
        detectSessionInUrl: false,
      },
    })
  : null;

// Cuando una Edge Function devuelve un código no-2xx, supabase-js devuelve un
// `FunctionsHttpError` cuyo mensaje genérico ("Edge Function returned a
// non-2xx status code") esconde el error real. Esta función extrae el mensaje
// real del cuerpo de la respuesta (que nuestras funciones devuelven como
// `{ error: "..." }`).
export async function resolveFunctionError(error: unknown): Promise<string> {
  const err = error as { context?: { json?: () => Promise<unknown> }; message?: string };
  if (err?.context && typeof err.context.json === 'function') {
    try {
      const ctx = (await err.context.json()) as Record<string, unknown>;
      const msg = ctx?.error || ctx?.message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    } catch {
      /* fall through to generic message */
    }
  }
  return err?.message || 'Error inesperado al contactar el servidor.';
}